import type { ContactInteractionType, User } from './contactInteraction';
import type { Copro } from './Copro';
import type { DPEDetail } from './DPE';

export interface Contact {
  id: number,
  type: ContactInteractionType,
  createdAt: string,
  updatedAt: string,
  user?: Partial<User>
}

interface Interact {
  id: number,
  dpeInitiator: number,
  sellOrRentWish: number,
  meetOrStopWish: number,
  comment: string,
  createdAt: string,
  updatedAt: string,
}

export const OWNER_STEPS = {
  NOT_CONTACTED: 'not-contacted',
  WAITING_FOR_FEEDBACK: 'waiting-for-feedback',
  TO_RECALL: 'to-recall',
  TO_CALL: 'to-call',
  REFUSED: 'refused',
  REFUSED_RENT_MANAGER: 'refused-rent-manager',
  REFUSED_SALES_MANAGER: 'refused-sales-manager',
  UNABLE_TO_REACH: 'unable-to-reach',
  UNABLE_TO_REACH_RENT_MANAGER: 'unable-to-reach-rent-manager',
  UNABLE_TO_REACH_SALES_MANAGER: 'unable-to-reach-sales-manager',
  TO_CONTACT_RENT_MANAGER: 'to-contact-rent-manager',
  TO_CONTACT_SALES_MANAGER: 'to-contact-sales-manager',
  WAITING_FOR_MEETING_RENT_MANAGER: 'waiting-for-meeting-rent-manager',
  WAITING_FOR_MEETING_FROM_INCOMING_CALL_RENT_MANAGER: 'waiting-for-meeting-ic-rent',
  WAITING_FOR_MEETING_SALES_MANAGER: 'waiting-for-meeting-sales-manager',
  WAITING_FOR_MEETING_FROM_INCOMING_CALL_SALES_MANAGER: 'waiting-for-meeting-ic-sales',
  CLOSED_RENT_MANAGER: 'closed-rent-manager',
  CLOSED_SALES_MANAGER: 'closed-sales-manager',
} as const;

export type OwnerStepType = typeof OWNER_STEPS[keyof typeof OWNER_STEPS];

export const OWNER_STEPS_LABELS = {
  [OWNER_STEPS.NOT_CONTACTED]: 'Non contacté',
  [OWNER_STEPS.WAITING_FOR_FEEDBACK]: 'En attente d\'un retour',
  [OWNER_STEPS.TO_RECALL]: 'Demande de rappel à traiter',
  [OWNER_STEPS.TO_CALL]: 'A appeler',
  [OWNER_STEPS.REFUSED]: 'Refus',
  [OWNER_STEPS.REFUSED_RENT_MANAGER]: 'Refus',
  [OWNER_STEPS.REFUSED_SALES_MANAGER]: 'Refus',
  [OWNER_STEPS.UNABLE_TO_REACH]: 'Injoignable',
  [OWNER_STEPS.UNABLE_TO_REACH_RENT_MANAGER]: 'Injoignable',
  [OWNER_STEPS.UNABLE_TO_REACH_SALES_MANAGER]: 'Injoignable',
  [OWNER_STEPS.TO_CONTACT_RENT_MANAGER]: 'A contacter',
  [OWNER_STEPS.TO_CONTACT_SALES_MANAGER]: 'A contacter',
  [OWNER_STEPS.WAITING_FOR_MEETING_RENT_MANAGER]: 'En attente du rendez-vous',
  [OWNER_STEPS.WAITING_FOR_MEETING_SALES_MANAGER]: 'En attente du rendez-vous',
  [OWNER_STEPS.CLOSED_RENT_MANAGER]: 'Suivi achevé',
  [OWNER_STEPS.CLOSED_SALES_MANAGER]: 'Suivi achevé',
};

// Extended DPE interface to match the API response
interface ExtendedDPE {
  id?: number;
  remoteId?: string;
  step?: string;
  copro?: {
    id: number;
    name: string;
    batchSize?: number;
    type?: number;
    remoteId?: string;
    validatedByUser?: boolean;
    validatedByAdmin?: boolean;
    addresses?: Array<{ address?: string; postalCode?: string; city?: string }>;
    [key: string]: unknown;
  };
  DPEQualifier?: {
    createdAt: string;
    updatedAt: string;
  };
  details?: DPEDetail;
  establishedAt?: string;
  receivedAt?: string;
  owners?: string[];
  treatedAt?: string;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  infoDPE?: { info?: unknown; [key: string]: unknown };
  // Additional fields from the original DPE interface that might be needed
  coproId?: number;
  coproName?: string;
  coproAddress?: string;
  assistant?: string;
  newDPE?: boolean;
  banLabel?: string;
}

export interface Consent {
  id: number;
  uuid: string;
  phone: string | null;
  sellOrRentWish: number | null;
  optin: boolean;
  recallAt: string | null;
  preferredTime: string | null;
  createdAt: string;
  updatedAt: string;
}

// Interface for the user information in interactions
interface InteractionUser {
  id: number;
  uuid: string;
  firstname: string;
  lastname: string;
  email: string;
  roles: string[];
  lastActivityAt: string;
  createdAt: string;
  updatedAt: string;
  DJUpdatedAt?: string | null;
  DJDeletedAt?: string | null;
}

// Lead interaction interface
export interface LeadInteraction {
  id: number;
  interactionAt: string;
  interactionWith: InteractionUser;
  signedFile: string | null;
  signedAt: string | null;
  professionalActionDone: boolean;
  clientClosed: boolean;
  data: Record<string, unknown> & Partial<{ length: number; amount: string | number; info: unknown; class: string }>;
  comment: string | null;
  createdAt: string;
  updatedAt: string;
}

// Sales manager interaction interface
export interface SalesManagerInteraction {
  id: number;
  interactionType: ContactInteractionType;
  interactionAt: string;
  meetingAt: string | null;
  meetingWith: string | null | User;
  meetingWithType?: string;
  comment: string | null;
  result?: string | null;
  amount?: string | null;
  createdAt: string;
  updatedAt: string;
  owner?: Owner;
}

// Rent manager interaction interface
export interface RentManagerInteraction {
  id: number;
  interactionType: ContactInteractionType;
  interactionAt: string;
  meetingAt: string | null;
  meetingWith: string | null;
  meetingWithType?: string;
  comment: string | null;
  result?: string | null;
  amount?: string | null;
  createdAt: string;
  updatedAt: string;
}

// Quality reference interaction interface
export interface QualityReferenceInteraction {
  id: number;
  interactionType: ContactInteractionType;
  interactionAt: string;
  comment: string | null;
  createdAt: string;
  updatedAt: string;
}

// Property type enum values (matches PropertyTypeEnum.php)
export const PROPERTY_TYPE = {
  UNKNOWN: 1,
  HOUSE: 2,
  APARTMENT: 3,
} as const;

export type PropertyType = typeof PROPERTY_TYPE[keyof typeof PROPERTY_TYPE];

export const PROPERTY_TYPE_LABELS: Record<PropertyType, string> = {
  [PROPERTY_TYPE.UNKNOWN]: 'Inconnu',
  [PROPERTY_TYPE.HOUSE]: 'Une maison',
  [PROPERTY_TYPE.APARTMENT]: 'Un appartement',
};

// Template labels for email/letter content (lowercase, without article)
export const PROPERTY_TYPE_TEMPLATE_LABELS: Record<PropertyType, string> = {
  [PROPERTY_TYPE.UNKNOWN]: 'appartement', // Fallback to appartement when unknown
  [PROPERTY_TYPE.HOUSE]: 'maison',
  [PROPERTY_TYPE.APARTMENT]: 'appartement',
};

// Helper function to get template label with null safety
export const getPropertyTypeTemplateLabel = (propertyType?: PropertyType | null): string => {
  if (propertyType === undefined || propertyType === null) {
    return 'appartement';
  }
  return PROPERTY_TYPE_TEMPLATE_LABELS[propertyType] || 'appartement';
};

export type OwnerSource = 'dpe' | 'incoming-call' | 'dpe-general-mailing';

export interface Owner {
  id? : number;
  uuid?: string;
  firstname?: string;
  lastname: string;
  postalCode?: string;
  city?: string;
  address?: string;
  livingIn?: boolean;
  email?: string;
  civility: number;
  phone?: string;
  floorNumber?: string;
  doorNumber: string;
  lotNumber?: string;
  surface?: number;
  propertyType?: PropertyType;
  contacts?: Contact[];
  interaction?: Interact;
  step: OwnerStepType;
  dpe?: ExtendedDPE;
  incomingCallCopro?: Copro;
  isPotentialOwner?: boolean;
  consents?: Consent[];
  leadInteractions?: LeadInteraction[];
  rentManagerInteractions?: RentManagerInteraction[];
  salesManagerInteractions?: SalesManagerInteraction[];
  qualityReferenceInteractions?: QualityReferenceInteraction[];
  source?: OwnerSource | null;
}
